import { Link, NavLink, Outlet, useParams } from 'react-router-dom';
import type { MiEvento } from '@eventos-cien/shared';
import { Callout } from '../components/Callout';
import { Eyebrow } from '../components/Eyebrow';
import { CargaPagina } from '../components/Spinner';
import { ThemeProvider } from '../theme/ThemeProvider';
import { PanelDataProvider, usePanelData } from './PanelDataProvider';
import { useMisEventos } from './useMisEventos';

export function PanelEventoLayout() {
  const { eventoId } = useParams<{ eventoId: string }>();
  const { eventos, error } = useMisEventos();

  if (!eventoId) return null;
  if (error) {
    return (
      <div className="p-4 sm:p-6">
        <Callout tono="error">{error}</Callout>
      </div>
    );
  }
  if (!eventos) return <CargaPagina />;

  const mio = eventos.find((e) => e.eventoId === eventoId);
  if (!mio) {
    return (
      <div className="p-4 sm:p-6">
        <Callout tono="alerta">No tienes acceso a este evento.</Callout>
      </div>
    );
  }

  return (
    <PanelDataProvider eventoId={eventoId} misRoles={mio.roles}>
      <ThemeProvider tema={mio.tema}>
        <PanelEventoContenido mio={mio} mostrarCambiarEvento={eventos.length > 1} />
      </ThemeProvider>
    </PanelDataProvider>
  );
}

function PanelEventoContenido({
  mio,
  mostrarCambiarEvento,
}: {
  mio: MiEvento;
  mostrarCambiarEvento: boolean;
}) {
  const { evento } = usePanelData();

  const tabs = [
    mio.roles.includes('asesor') && { to: 'invitaciones', texto: 'Mis invitaciones' },
    mio.roles.includes('asesor') && { to: 'invitados', texto: 'Invitados' },
    mio.roles.includes('staff') && { to: 'check-in', texto: 'Check-in' },
  ].filter((t): t is { to: string; texto: string } => Boolean(t));

  return (
    <div className="mx-auto max-w-2xl p-4 sm:p-6">
      <div className="flex items-start justify-between gap-3">
        <div>
          <Eyebrow>Mi panel</Eyebrow>
          <h1 className="mt-1 text-xl font-semibold text-texto">{evento?.nombre || mio.nombre}</h1>
        </div>
        {mostrarCambiarEvento && (
          <Link to="/panel" className="text-sm font-medium text-navy underline underline-offset-2">
            Cambiar evento
          </Link>
        )}
      </div>

      <nav className="mt-5 flex gap-1 overflow-x-auto border-b border-borde pb-px">
        {tabs.map((t) => (
          <NavLink
            key={t.to}
            to={t.to}
            className={({ isActive }) =>
              [
                'shrink-0 whitespace-nowrap rounded-t-lg px-3.5 py-2.5 text-sm font-medium transition',
                isActive ? 'border-b-2 border-navy text-navy' : 'text-texto/50 hover:text-texto',
              ].join(' ')
            }
          >
            {t.texto}
          </NavLink>
        ))}
      </nav>

      <div className="pt-5">
        <Outlet />
      </div>
    </div>
  );
}
